import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import ViewHeader from '../components/ViewHeader';
import KpiCard from '../components/KpiCard';

const kpis = [
    { title: 'Dr ali', value: '5000', unit: 'JOD', change: '8%', changeType: 'up' },
    { title: 'Admissions', value: '200', change: '9%', changeType: 'up' },
    { title: 'Procedures', value: '500', change: '4%', changeType: 'up' },
    { title: 'average', value: '7000', unit: 'JOD', change: '7%', changeType: 'up' },
];

const revenueBars = [
    { label: 'Dr. ali', value: 100 },
    { label: 'Dr. ahmad', value: 50 },
    { label: 'Dr .Haya', value: 70 },
];

const maxRevenue = Math.max(...revenueBars.map(b => b.value));

const revenueTimeData = [
    { name: 'jan', 'dr ali': 4.3, 'dr ahmad': 6.7, 'dr haya': 8.8 },
    { name: 'feb', 'dr ali': 2.5, 'dr ahmad': 6.9, 'dr haya': 9.0 },
    { name: 'mar', 'dr ali': 3.5, 'dr ahmad': 5.4, 'dr haya': 8.3 },
    { name: 'apr', 'dr ali': 4.5, 'dr ahmad': 7.3, 'dr haya': 12.5 },
    { name: 'may', 'dr ali': 3.5, 'dr ahmad': 5.3, 'dr haya': 8.4 },
    { name: 'jun', 'dr ali': 4.5, 'dr ahmad': 7.3, 'dr haya': 12.5 },
];

const salesPie = [
    { name: 'medical services', value: 55 },
    { name: 'non medical services', value: 30 },
    { name: 'medications', value: 15 },
];

const VET_COLORS = ['#0f4c5c', '#f97316', '#10b981'];
const PIE_COLORS = ['#0f4c5c', '#f97316', '#10b981'];

const RevenueBar = ({ label, value, max }) => {
    const [width, setWidth] = React.useState(0);
    const target = (value / max) * 100;

    React.useEffect(() => {
        const timer = setTimeout(() => setWidth(target), 700);
        return () => clearTimeout(timer);
    }, [target]);

    return (
        <div className="clinic-dashboard-revbar">
            <span className="clinic-dashboard-revbar-label">{label}</span>
            <div className="clinic-dashboard-revbar-track">
                <div
                    className="clinic-dashboard-revbar-fill"
                    style={{ width: `${width}%`, transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)' }}
                />
            </div>
            <span className="clinic-dashboard-revbar-value">{value} JD</span>
        </div>
    );
};

const VeterinarianProductivity = ({ onBack }) => {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, minHeight: 0 }}>
            <ViewHeader title="Veterinarian Productivity" subtitle="2026" onBack={onBack} />

            {/* Top row */}
            <div style={{ display: 'flex', gap: '16px' }}>
                {/* Key metrics */}
                <div className="clinic-dashboard-section-card" style={{ flex: 1, animationDelay: '0ms' }}>
                    <div className="clinic-dashboard-section-title">Key metrics</div>
                    <div className="clinic-dashboard-kpi-row four">
                        {kpis.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
                    </div>
                </div>

                {/* Average revenue by visit */}
                <div className="clinic-dashboard-section-card" style={{ flex: 0.7, display: 'flex', flexDirection: 'column', justifyContent: 'center', animationDelay: '80ms' }}>
                    <div className="clinic-dashboard-section-title">Average revenue by visit</div>
                    <div className="clinic-dashboard-revbar-list">
                        {revenueBars.map((bar, i) => (
                            <RevenueBar key={i} {...bar} max={maxRevenue} />
                        ))}
                    </div>
                </div>
            </div>

            {/* Bottom row */}
            <div style={{ display: 'flex', gap: '16px', flex: 1, minHeight: 0 }}>
                {/* Revenue overtime */}
                <div className="clinic-dashboard-section-card" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', animationDelay: '160ms' }}>
                    <div className="clinic-dashboard-section-title">Revenue overtime</div>
                    <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '200px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={revenueTimeData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis
                                    dataKey="name"
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#9ca3af', fontSize: 12 }}
                                />
                                <Tooltip
                                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                />
                                <Line animationBegin={600} type="monotone" dataKey="dr ali" stroke={VET_COLORS[0]} strokeWidth={2} dot={{ r: 3 }} />
                                <Line animationBegin={600} type="monotone" dataKey="dr ahmad" stroke={VET_COLORS[1]} strokeWidth={2} dot={{ r: 3 }} />
                                <Line animationBegin={600} type="monotone" dataKey="dr haya" stroke={VET_COLORS[2]} strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Sales pie */}
                <div className="clinic-dashboard-section-card" style={{ flex: 0.6, minHeight: 0, display: 'flex', flexDirection: 'column', animationDelay: '240ms' }}>
                    <div className="clinic-dashboard-section-title">Sales</div>
                    <div className="clinic-dashboard-chart-wrap" style={{ minHeight: '180px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <ResponsiveContainer width="100%" height="75%">
                            <PieChart>
                                <Pie
                                    data={salesPie}
                                    outerRadius={75}
                                    paddingAngle={1}
                                    dataKey="value"
                                    stroke="#fff"
                                    strokeWidth={1}
                                >
                                    {salesPie.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="clinic-dashboard-legend" style={{ flexWrap: 'wrap', justifyContent: 'center', gap: '8px 12px' }}>
                            {salesPie.map((entry, index) => (
                                <div key={index} className="clinic-dashboard-legend-item">
                                    <span className="clinic-dashboard-legend-dot" style={{ backgroundColor: PIE_COLORS[index] }} />
                                    <span className="clinic-dashboard-legend-label">{entry.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VeterinarianProductivity;
